import { useMutation } from '@tanstack/react-query';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { useDriverStateV2 } from '../../hooks/useDriverStateV2';
import env from '../../config/env';
import type { OfficeRole } from '@relay-towing/api';

interface SendMemberInvitationData {
  email: string;
  /** Display name for the invitation email greeting (optional for legacy callers). */
  inviteeName?: string;
  /** The office role they join with; the server makes it Dispatch. */
  officeRole?: OfficeRole;
}

export const sendMemberInvitation = async (
  token: string,
  companyId: string,
  data: SendMemberInvitationData
) => {
  const response = await fetch(`${env.apiUrl}/member-invitations/send`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-company-id': companyId,
    },
    body: JSON.stringify({
      email: data.email,
      ...(data.officeRole ? { officeRole: data.officeRole } : {}),
      ...(data.inviteeName?.trim()
        ? { inviteeName: data.inviteeName.trim() }
        : {}),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to send invitation: ${response.statusText}`
    );
  }

  return await response.json();
};

export const useSendMemberInvitation = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();

  return useMutation({
    mutationFn: async (data: SendMemberInvitationData) => {
      if (!driver?.companyId) {
        throw new Error('Company ID not available');
      }

      const token = await getAccessTokenSilently();
      return sendMemberInvitation(token, driver.companyId, data);
    },
  });
};
